import { Suspense } from 'react';
import { useTranslation } from 'react-i18next';
import { add } from 'date-fns';
import { Chip, cn, Label, Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@c2fo/liquidity';
import Skeleton from '@/enterprise/components/Skeleton';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import { useReporting } from '@/reporting';
import { dateAsIsoString } from '@/utils/dateAsIsoString';
import { Cadence, CadenceType, DaysOfTheWeek, WeeklyCadenceValue } from '../types';
import { useOfferModalClearSchedules } from '../useOfferModalClearSchedules';

interface CadenceOptions {
  label: string;
  type: CadenceType;
}

const cadenceOptions: CadenceOptions[] = [
  { label: 'accountSettings.notificationSettingsFrequencyDaily', type: 'daily' },
  { label: 'accountSettings.notificationSettingsFrequencyWeekly', type: 'weekly' },
];

const WeekdaySelection = ({
  takerMarkets,
  cadence,
  startDate,
  endDate,
  setCadence,
}: {
  takerMarkets: TakerMarket[];
  cadence: Cadence;
  startDate?: string;
  endDate?: string;
  setCadence: (cadence: Cadence) => void;
}) => {
  const { trackEnterpriseEvent } = useReporting();
  // If end date is provided, use it, otherwise set the end date to one month from start date or one month from now in ISO date format if start date is not provided
  // Example: If today is Jul 28 2025 then end date is 2025-08-28
  // This is done to limit the number of clear schedules returned by the API as
  // it is assumed a market has consistent days of the week clears month to month
  const endDateString = endDate
    ? endDate
    : dateAsIsoString(add(startDate ? new Date(startDate) : new Date(), { months: 1 }));
  const finalClears = useOfferModalClearSchedules({ takerMarkets, startDate, endDate: endDateString });
  const { t } = useTranslation();

  // Get the unique days of the week that each market clears on
  const clearDaySetsByMarket = finalClears
    .map((market) => {
      return new Set(market.clearSchedules.map((clear) => clear.clearTimeDayOfWeekInScheduleTimeZone));
    })
    .filter((set) => set.size > 0);

  // Get the days of the week that are shared by all included markets
  const commonDaysOfWeek =
    clearDaySetsByMarket.length > 0
      ? [
          ...clearDaySetsByMarket.reduce((acc, currSet) => {
            return new Set([...acc].filter((day) => currSet.has(day)));
          }),
        ].sort((a, b) => {
          return DaysOfTheWeek.indexOf(a as WeeklyCadenceValue) - DaysOfTheWeek.indexOf(b as WeeklyCadenceValue);
        })
      : [];

  // We want to be careful here not to hide a day if somehow it was already set in the platform
  const filteredDaysOfWeek = DaysOfTheWeek.filter((day) => {
    if (day === 'Saturday' && !cadence.value.includes('Saturday')) {
      return false;
    }

    if (day === 'Sunday' && !cadence.value.includes('Sunday')) {
      return false;
    }

    return true;
  });

  const onClick = (day: WeeklyCadenceValue) => {
    const isCurrentlySelected = cadence.value?.find((val) => val === day);

    if (isCurrentlySelected) {
      // Remove the day from the selected days
      setCadence({ ...cadence, value: cadence.value.filter((val) => val !== day) });
      trackEnterpriseEvent('offer::schedule::form::dayOfWeek::deselected', {
        dayOfWeek: day,
      });
    } else {
      // Add the day to the selected days
      setCadence({ ...cadence, value: [...(cadence.value ? cadence.value : []), day] });
      trackEnterpriseEvent('offer::schedule::form::dayOfWeek::selected', {
        dayOfWeek: day,
      });
    }
  };

  return (
    <>
      <div>
        <Label>{t('scheduling.on')}</Label>
        <div className="flex flex-wrap gap-2 pt-1">
          {filteredDaysOfWeek.map((day) => (
            <Chip
              key={day}
              label={t(`scheduling.daysOfWeek.${day}`)}
              type="info"
              isRound
              size="lg"
              variant={cadence.value?.find((val) => val === day) ? 'filled' : 'outlined'}
              // Disable when market does not clear on given day AND day is not already selected
              disabled={!commonDaysOfWeek.includes(day) && !cadence.value?.find((val) => val === day)}
              onClick={() => onClick(day)}
            />
          ))}
        </div>
      </div>

      <p className="text-sm text-text-secondary">
        {commonDaysOfWeek.length > 0 ? (
          <>
            {t('scheduling.marketProcessingProjection', {
              daysOfWeekCommaSeparated: commonDaysOfWeek.map((day) => t(`scheduling.daysOfWeek.${day}`)).join(', '),
            })}
          </>
        ) : (
          <>{t('scheduling.noProcessingDays')}</>
        )}
      </p>
    </>
  );
};

const WeekdaySelectionSkeleton = () => {
  return (
    <div className="space-y-3">
      <Skeleton className="h-6 w-12 rounded-3xl" />
      <div className="flex flex-wrap gap-2">
        <Skeleton className="h-12 w-24 rounded-3xl" />
        <Skeleton className="h-12 w-24 rounded-3xl" />
        <Skeleton className="h-12 w-24 rounded-3xl" />
        <Skeleton className="h-12 w-24 rounded-3xl" />
        <Skeleton className="h-12 w-24 rounded-3xl" />
      </div>
      <Skeleton className="h-6 w-[75%] rounded-3xl" />
    </div>
  );
};

const CadenceInput = ({
  takerMarkets,
  cadence,
  startDate,
  endDate,
  setCadence,
  errors,
}: {
  takerMarkets: TakerMarket[];
  cadence: Cadence;
  startDate?: string;
  endDate?: string;
  setCadence: (cadence: Cadence) => void;
  errors: {
    cadence?: {
      message: string;
    };
  };
}) => {
  const { trackEnterpriseEvent } = useReporting();
  const { t } = useTranslation();

  return (
    <div className="space-y-4">
      <div className="space-y-1">
        <Label id="cadence-label">{t('core.repeat')}</Label>
        <Select
          onValueChange={(value) => {
            setCadence({ type: value as CadenceType, value: value === 'daily' ? DaysOfTheWeek : [] });
            trackEnterpriseEvent('offer::schedule::form::cadence::selected', {
              cadence: value,
            });
          }}
          value={cadence.type}
        >
          <SelectTrigger aria-label="cadence" className="w-64" aria-labelledby="cadence-label">
            <SelectValue defaultValue="daily" />
          </SelectTrigger>
          <SelectContent>
            {cadenceOptions.map(({ label, type }) => (
              <SelectItem key={type} value={type}>
                {t(label)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {cadence.type === 'weekly' && (
        <div className="space-y-1">
          <div
            className={cn('space-y-4 rounded-lg border border-blue-100 bg-blue-100 p-4 sm:p-6', {
              'border-red-600': !!errors.cadence,
            })}
          >
            <Suspense fallback={<WeekdaySelectionSkeleton />}>
              <WeekdaySelection
                takerMarkets={takerMarkets}
                cadence={cadence}
                startDate={startDate}
                endDate={endDate}
                setCadence={setCadence}
              />
            </Suspense>
          </div>
          {!!errors.cadence && <p className="text-sm text-red-600">{errors.cadence.message}</p>}
        </div>
      )}
    </div>
  );
};

export default CadenceInput;
